<script>
import CrmMultiSelectSearch from 'dashboard/components/crm/CrmMultiSelectSearch.vue'

export default {
  name: 'DealFormField',
  components: { CrmMultiSelectSearch },
  props: {
    field: { type: Object, required: true },
    modelValue: { default: null },
    otherSpecifyValue: { type: String, default: '' },
    readonly: { type: Boolean, default: false },
    availableProducts: { type: Array, default: () => [] },
    isComexContact: { type: Boolean, default: false },
  },
  emits: ['update:modelValue', 'update:otherSpecifyValue'],
  computed: {
    fieldOptions() {
      if (this.field.updates_deal_field === 'product_ids') {
        return this.availableProducts.map(p => ({
          id: p.id,
          name: p.name + (p.formatted_price ? ' - ' + p.formatted_price : ''),
        }))
      }
      if (this.field.updates_deal_field === 'payment_method_ids') {
        return (this.field.options || []).map((name, index) => ({ id: index, name }))
      }
      let options = []
      if (this.field.comex_options && this.isComexContact) {
        options = [...this.field.comex_options]
      } else {
        options = [...(this.field.options || [])]
      }
      if (this.field.allowOther && !options.includes('Otro')) {
        options.push('Otro')
      }
      return options
    },
    showOtherField() {
      if (!this.field.allowOther) return false
      const value = this.modelValue
      if (this.field.type === 'select') return value === 'Otro'
      if (this.field.type === 'multiselect') return Array.isArray(value) && value.includes('Otro')
      return false
    },
    isSpecialMultiselect() {
      return (
        this.field.type === 'multiselect' &&
        (this.field.updates_deal_field === 'product_ids' ||
          this.field.updates_deal_field === 'payment_method_ids')
      )
    },
    displayValue() {
      const val = this.modelValue
      if (val === null || val === undefined || val === '') return '—'
      if (this.field.type === 'checkbox') return val ? 'Sí' : 'No'
      if (this.field.type === 'date') {
        try {
          return new Date(val).toLocaleDateString('es-AR', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        } catch {
          return val
        }
      }
      if (Array.isArray(val)) {
        if (!val.length) return '—'
        if (typeof val[0] === 'object' && val[0] !== null && val[0].name) {
          return val.map(v => v.name).join(', ')
        }
        const parts = val.filter(v => v !== 'Otro')
        if (this.showOtherField && this.otherSpecifyValue) {
          return [...parts, `Otro: ${this.otherSpecifyValue}`].join(', ')
        }
        return parts.join(', ') || '—'
      }
      if (val === 'Otro' && this.showOtherField && this.otherSpecifyValue) {
        return `Otro: ${this.otherSpecifyValue}`
      }
      return String(val)
    },
  },
  methods: {
    toggleOption(option) {
      const current = Array.isArray(this.modelValue) ? [...this.modelValue] : []
      const idx = current.indexOf(option)
      if (idx > -1) current.splice(idx, 1)
      else current.push(option)
      this.$emit('update:modelValue', current)
    },
    isOptionSelected(option) {
      return Array.isArray(this.modelValue) && this.modelValue.includes(option)
    },
  },
}
</script>

<template>
  <!-- Readonly / display mode -->
  <p v-if="readonly" class="text-sm text-n-slate-12 py-1">
    {{ displayValue }}
  </p>

  <!-- Edit mode -->
  <template v-else>
    <!-- Text -->
    <input
      v-if="field.type === 'text'"
      :id="field.key"
      :value="modelValue"
      type="text"
      :placeholder="field.placeholder || ''"
      :required="field.required"
      class="w-full px-3 py-2 border border-slate-300 dark:border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-2 text-slate-900 dark:text-slate-100"
      @input="$emit('update:modelValue', $event.target.value)"
    />

    <!-- Number -->
    <input
      v-else-if="field.type === 'number'"
      :id="field.key"
      :value="modelValue"
      type="number"
      :placeholder="field.placeholder || ''"
      :required="field.required"
      :min="field.min"
      :max="field.max"
      :step="field.step || 'any'"
      class="w-full px-3 py-2 border border-slate-300 dark:border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-2 text-slate-900 dark:text-slate-100"
      @input="$emit('update:modelValue', $event.target.valueAsNumber)"
    />

    <!-- Textarea -->
    <textarea
      v-else-if="field.type === 'textarea'"
      :id="field.key"
      :value="modelValue"
      :placeholder="field.placeholder || ''"
      :required="field.required"
      :rows="field.rows || 3"
      class="w-full px-3 py-2 border border-slate-300 dark:border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-2 text-slate-900 dark:text-slate-100"
      @input="$emit('update:modelValue', $event.target.value)"
    />

    <!-- Select -->
    <select
      v-else-if="field.type === 'select'"
      :id="field.key"
      :value="modelValue"
      :required="field.required"
      class="w-full px-3 py-2 border border-slate-300 dark:border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-2 text-slate-900 dark:text-slate-100"
      @change="$emit('update:modelValue', $event.target.value)"
    >
      <option value="">Seleccione una opción</option>
      <option v-for="option in fieldOptions" :key="option" :value="option">
        {{ option }}
      </option>
    </select>

    <!-- Multiselect — products or payment methods (CrmMultiSelectSearch) -->
    <CrmMultiSelectSearch
      v-else-if="isSpecialMultiselect"
      :model-value="modelValue || []"
      :options="fieldOptions"
      :placeholder="
        field.updates_deal_field === 'product_ids'
          ? 'Seleccionar productos'
          : 'Seleccionar métodos de pago'
      "
      @update:model-value="$emit('update:modelValue', $event)"
    />

    <!-- Multiselect — generic (checkboxes list) -->
    <div v-else-if="field.type === 'multiselect'" class="space-y-2">
      <label
        v-for="option in fieldOptions"
        :key="option"
        class="flex items-start gap-2 p-2 hover:bg-slate-50 dark:hover:bg-n-solid-2 rounded cursor-pointer"
      >
        <input
          type="checkbox"
          :checked="isOptionSelected(option)"
          class="mt-0.5"
          @change="toggleOption(option)"
        />
        <span class="text-sm text-slate-700 dark:text-slate-200">{{ option }}</span>
      </label>
    </div>

    <!-- Checkbox -->
    <label
      v-else-if="field.type === 'checkbox'"
      class="flex items-start gap-2 cursor-pointer"
    >
      <input
        :id="field.key"
        :checked="modelValue"
        type="checkbox"
        class="mt-0.5"
        @change="$emit('update:modelValue', $event.target.checked)"
      />
      <span class="text-sm text-slate-700 dark:text-slate-200">
        {{ field.checkedLabel || field.label }}
      </span>
    </label>

    <!-- Date -->
    <input
      v-else-if="field.type === 'date'"
      :id="field.key"
      :value="modelValue"
      type="date"
      :required="field.required"
      :min="field.minDate"
      :max="field.maxDate"
      class="w-full px-3 py-2 border border-slate-300 dark:border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-2 text-slate-900 dark:text-slate-100"
      @change="$emit('update:modelValue', $event.target.value)"
    />

    <!-- Conditional "Otro" specify field -->
    <div v-if="showOtherField" class="mt-3 pl-1 border-l-2 border-woot-500">
      <label
        :for="`${field.key}_other_specify`"
        class="block text-sm font-medium text-slate-700 dark:text-slate-200 mb-1"
      >
        Especifique "Otro":
      </label>
      <input
        :id="`${field.key}_other_specify`"
        :value="otherSpecifyValue"
        type="text"
        placeholder="Escriba aquí..."
        class="w-full px-3 py-2 border border-slate-300 dark:border-n-weak rounded-md focus:outline-none focus:ring-2 focus:ring-woot-500 bg-white dark:bg-n-solid-2 text-slate-900 dark:text-slate-100"
        @input="$emit('update:otherSpecifyValue', $event.target.value)"
      />
    </div>
  </template>
</template>

<style scoped>
/* Prefer Tailwind */
</style>
